import { getAddModuleType, MetadataModules } from '../../enums';
import { addAddProvider, addTypedProvider, clear, clearQuery, getMetaData, } from '../../metaData';
import { BASE_IMAGE_ID, FRAME_IMAGE_IDS } from './imageIdsProviders';
export class CacheData {
    static setCacheDataInternal(type, query, value) {
        let valueMap = this.mapCacheData.get(type);
        if (!valueMap) {
            valueMap = new Map();
            this.mapCacheData.set(type, valueMap);
        }
        valueMap.set(query, value);
        this.clearRelatedDerivedCache(type, query);
    }
    static clearRelatedDerivedCache(type, query) {
        const derivedTypes = this.secondaryTypesByBaseType.get(type);
        if (!derivedTypes?.size) {
            return;
        }
        const frameImageIds = getMetaData(FRAME_IMAGE_IDS, query) ??
            new Set([query]);
        for (const frameImageId of frameImageIds) {
            for (const derivedType of derivedTypes) {
                const typeMap = this.mapCacheData.get(derivedType);
                typeMap?.delete(frameImageId);
            }
        }
    }
    static registerSecondaryTypes(secondaryType, secondaryOf) {
        if (!secondaryOf) {
            return;
        }
        const baseTypes = Array.isArray(secondaryOf) ? secondaryOf : [secondaryOf];
        for (const baseType of baseTypes) {
            let secondaryTypes = this.secondaryTypesByBaseType.get(baseType);
            if (!secondaryTypes) {
                secondaryTypes = new Set();
                this.secondaryTypesByBaseType.set(baseType, secondaryTypes);
            }
            secondaryTypes.add(secondaryType);
        }
    }
    static getCacheData(type, query) {
        return this.mapCacheData.get(type)?.get(query);
    }
    static hasCacheData(type, query) {
        return this.mapCacheData.get(type)?.has(query) === true;
    }
    static clearCacheData() {
        this.mapCacheData.clear();
        this.secondaryTypesByBaseType.clear();
        clear(FRAME_IMAGE_IDS);
        clear(BASE_IMAGE_ID);
    }
    static clearTypedCacheData(type, query) {
        const secondaryTypes = this.secondaryTypesByBaseType.get(type);
        const valueMap = this.mapCacheData.get(type);
        if (query) {
            valueMap?.delete(query);
            if (secondaryTypes?.size) {
                for (const secondaryType of secondaryTypes) {
                    this.mapCacheData.get(secondaryType)?.delete(query);
                }
            }
            if (type === MetadataModules.NATURALIZED || secondaryTypes?.size) {
                clearQuery(FRAME_IMAGE_IDS, query);
                clearQuery(BASE_IMAGE_ID, query);
            }
            return;
        }
        valueMap?.clear();
        if (secondaryTypes?.size) {
            for (const secondaryType of secondaryTypes) {
                this.mapCacheData.get(secondaryType)?.clear();
            }
        }
        if (type === MetadataModules.NATURALIZED || secondaryTypes?.size) {
            clear(FRAME_IMAGE_IDS);
            clear(BASE_IMAGE_ID);
        }
    }
    static fromAsyncLookup(type, query, lookup, options) {
        if (options?.noCache !== true && options?.reCache !== true) {
            const cachedValue = this.getCacheData(type, query);
            if (cachedValue !== undefined) {
                return cachedValue;
            }
        }
        const lookupValue = lookup();
        if (lookupValue === undefined) {
            return undefined;
        }
        if (!(lookupValue instanceof Promise)) {
            if (!options?.noCache) {
                this.setCacheDataInternal(type, query, lookupValue);
            }
            return lookupValue;
        }
        return lookupValue.then((resolvedValue) => {
            if (resolvedValue !== undefined && !options?.noCache) {
                this.setCacheDataInternal(type, query, resolvedValue);
            }
            return resolvedValue;
        });
    }
    createTypeCacheProvider(type) {
        return createTypeCacheProvider(type);
    }
    clearCacheData() {
        CacheData.clearCacheData();
    }
    clearTypedCacheData(type, query) {
        CacheData.clearTypedCacheData(type, query);
    }
    getCacheData(type, query) {
        return CacheData.getCacheData(type, query);
    }
    hasCacheData(type, query) {
        return CacheData.hasCacheData(type, query);
    }
    fromAsyncLookup(type, query, lookup, options) {
        return CacheData.fromAsyncLookup(type, query, lookup, options);
    }
}
CacheData.mapCacheData = new Map();
CacheData.secondaryTypesByBaseType = new Map();
export class WritableCacheData extends CacheData {
    static setCacheData(type, query, value) {
        this.setCacheDataInternal(type, query, value);
    }
    setCacheData(type, query, value) {
        WritableCacheData.setCacheData(type, query, value);
    }
}
export const cacheData = new WritableCacheData();
export function createTypeCacheProvider(type) {
    return (next, query, data, options) => {
        return CacheData.fromAsyncLookup(type, query, () => next(query, data, options), options);
    };
}
export function createTypeWritableCacheProvider(type) {
    const addType = getAddModuleType(type);
    return (next, query, data, options) => {
        const cachedValue = CacheData.getCacheData(type, query);
        if (cachedValue !== undefined) {
            console.warn(`Metadata add skipped for "${type}" at query "${query}" because cache already has a value.`);
            return cachedValue;
        }
        const addCachedValue = CacheData.getCacheData(addType, query);
        if (addCachedValue !== undefined) {
            return addCachedValue;
        }
        const nextValue = next(query, data, options);
        if (nextValue === undefined) {
            return undefined;
        }
        if (!(nextValue instanceof Promise)) {
            WritableCacheData.setCacheData(type, query, nextValue);
            return nextValue;
        }
        const managedPromise = nextValue
            .then((resolvedValue) => {
            if (resolvedValue !== undefined) {
                WritableCacheData.setCacheData(type, query, resolvedValue);
            }
            return resolvedValue;
        })
            .finally(() => {
            CacheData.clearTypedCacheData(addType, query);
        });
        WritableCacheData.setCacheData(addType, query, managedPromise);
        return managedPromise;
    };
}
export function clearCacheData() {
    CacheData.clearCacheData();
}
export function clearTypedCacheData(type, query) {
    CacheData.clearTypedCacheData(type, query);
}
export function setCacheData(type, query, value) {
    WritableCacheData.setCacheData(type, query, value);
}
export function getCacheData(type, query) {
    return CacheData.getCacheData(type, query);
}
export function hasCacheData(type, query) {
    return CacheData.hasCacheData(type, query);
}
export function fromAsyncLookup(type, query, lookup, options) {
    return CacheData.fromAsyncLookup(type, query, lookup, options);
}
export function addCacheForType(type, options) {
    const { secondaryOf, ...providerOptions } = (options ??
        {});
    CacheData.registerSecondaryTypes(type, secondaryOf);
    addTypedProvider(type, createTypeCacheProvider(type), {
        priority: 50_000,
        clear: clearTypedCacheData.bind(null, type),
        clearQuery: clearTypedCacheData.bind(null, type),
        ...providerOptions,
    });
}
export function addWritableCacheForType(type, options) {
    const addType = getAddModuleType(type);
    const { secondaryOf, ...providerOptions } = (options ??
        {});
    addCacheForType(type, { secondaryOf, ...providerOptions });
    CacheData.registerSecondaryTypes(addType, type);
    addAddProvider(type, createTypeWritableCacheProvider(type), {
        priority: 50_000,
        clear: clearTypedCacheData.bind(null, addType),
        clearQuery: clearTypedCacheData.bind(null, addType),
        ...providerOptions,
    });
}
export function registerCacheProviders() {
    addCacheForType(BASE_IMAGE_ID);
    addCacheForType(FRAME_IMAGE_IDS);
    addWritableCacheForType(MetadataModules.NATURALIZED);
    addCacheForType(MetadataModules.INSTANCE, {
        secondaryOf: MetadataModules.NATURALIZED,
    });
    addCacheForType(MetadataModules.URI_MODULE, {
        secondaryOf: MetadataModules.NATURALIZED,
    });
    addCacheForType(MetadataModules.IMAGE_PLANE, {
        secondaryOf: MetadataModules.NATURALIZED,
    });
    addCacheForType(MetadataModules.FRAME_MODULE, {
        secondaryOf: MetadataModules.NATURALIZED,
    });
    addCacheForType(MetadataModules.GENERAL_IMAGE, {
        secondaryOf: MetadataModules.NATURALIZED,
    });
}
